import {
	type AggRequest,
	type AggResponse,
	fetchAgg,
} from '../../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../../utils/caching/in-memory';

import {
	pastMeetingOccurrencesCypherQueryGenerator,
	type PastMeetingOccurrencesCypherQueryVariables,
} from './query-generator';
import {
	type PastMeetingCypherQueryData,
	type PastMeetingOccurrence,
	type PastMeetingOccurrencesMap,
} from './types';


/**
 * Cypher query: given a video ARI, walk up to its parent recurring CalendarEvent,
 * then return up to 5 sibling past-occurrence videos ordered by meeting date DESC.
 *
 * Variables: $videoAri — the ARI of the current meeting video.
 */
const PAST_MEETING_OCCURRENCES_CYPHER_QUERY = `
MATCH (video:GraphVideo {ari: $videoAri})
    <-[:external_calendar_event_links_external_video]-(child:GraphCalendarEvent)
    <-[:external_calendar_event_has_child_external_calendar_event]-(parent:GraphCalendarEvent)
MATCH (parent)-[:external_calendar_event_has_child_external_calendar_event]->(sibling:GraphCalendarEvent)
    -[:external_calendar_event_links_external_video]->(siblingVideo:GraphVideo)
WHERE sibling.createdAt < child.createdAt
RETURN siblingVideo
ORDER BY sibling.createdAt DESC
LIMIT 5
`;

const cache = inMemoryCache<AggResponse<PastMeetingCypherQueryData>>();

const expireOnErrorOrEmpty = <T>(result: AggResponse<T>) => {
	const { data, errors } = result;
	return !data || (!!errors && errors.length > 0);
};

/**
 * Extracts past meeting occurrences from a cypherQueryV2 response.
 * Each edge's column value contains an ExternalVideo node with url, displayName, createdAt.
 */
function parsePastMeetingOccurrences(
	response: AggResponse<PastMeetingCypherQueryData>,
): PastMeetingOccurrence[] {
	const edges = response.data?.graphStore?.cypherQueryV2?.edges;
	if (!edges || edges.length === 0) {
		return [];
	}

	const occurrences: PastMeetingOccurrence[] = [];
	for (const edge of edges) {
		for (const column of edge.node.columns) {
			const data = column.value?.data;
			if (data?.url) {
				occurrences.push({
					url: data.url,
					title: data.displayName ?? null,
					lastModifiedDate: data.createdAt ?? null,
					thirdPartyId: data.thirdPartyId ?? null,
					providerId: data.provider?.providerId ?? null,
				});
			}
		}
	}
	return occurrences;
}

export type FetchPastMeetingOccurrencesProps = Pick<
	AggRequest<Record<string, unknown>>,
	'aggAbsoluteUrl' | 'headers'
>;

/**
 * Fetches the 5 most recent past occurrences of a recurring MS Teams meeting,
 * given the ARI of the current meeting video.
 *
 * Uses an in-memory cache keyed by videoAri to avoid duplicate fetches.
 *
 * @param videoAri - ARI of the current ExternalVideo (SearchResultGraphDocument.id)
 * @param queryContext - Site ARI for TWG workspace routing (e.g. "ari:cloud:platform::site/<cloudId>")
 * @param fetchProps - AGG request config (aggAbsoluteUrl, headers)
 *
 */
export async function fetchPastMeetingOccurrences(
	videoAri: string,
	queryContext: string,
	{ aggAbsoluteUrl, headers }: FetchPastMeetingOccurrencesProps,
): Promise<PastMeetingOccurrence[]> {
	const graphQuery = pastMeetingOccurrencesCypherQueryGenerator();
	const variables: PastMeetingOccurrencesCypherQueryVariables = {
		cypherQuery: PAST_MEETING_OCCURRENCES_CYPHER_QUERY,
		params: { videoAri },
		queryContext,
	};

	const response = await cache.inMemoryDecorator(
		generateHashFromVariables({ videoAri, queryContext }),
		() =>
			fetchAgg<PastMeetingOccurrencesCypherQueryVariables, PastMeetingCypherQueryData>({
				variables,
				aggAbsoluteUrl,
				graphQuery,
				operationName: 'SearchPagePastMeetingOccurrencesHydration',
				headers,
			}),
		expireOnErrorOrEmpty,
	);

	return parsePastMeetingOccurrences(response);
}

/**
 * Fetches past meeting occurrences for multiple video ARIs in parallel.
 *
 * @param videoAris - Array of ExternalVideo ARIs to look up
 * @param queryContext - Site ARI for TWG workspace routing (e.g. "ari:cloud:platform::site/<cloudId>")
 * @param fetchProps - AGG request config (aggAbsoluteUrl, headers)
 * @returns Map of videoAri → past meeting occurrences (null if fetch failed for that ARI)
 */
export async function fetchPastMeetingOccurrencesBatch(
	videoAris: string[],
	queryContext: string,
	fetchProps: FetchPastMeetingOccurrencesProps,
): Promise<PastMeetingOccurrencesMap> {
	const results: PastMeetingOccurrencesMap = {};

	const promises = videoAris.map(async (videoAri) => {
		try {
			const occurrences = await fetchPastMeetingOccurrences(videoAri, queryContext, fetchProps);
			results[videoAri] = occurrences;
		} catch {
			results[videoAri] = null;
		}
	});

	await Promise.all(promises);
	return results;
}
